'use strict';
import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';

const btnStyle = {
  padding: '8px 16px', background: 'linear-gradient(135deg,#6366f1,#4f46e5)',
  color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px',
  fontWeight: '600', cursor: 'pointer'
};
const cardStyle = { border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', background: 'white' };
const inputStyle = { padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px' };

function Msg({ msg }) {
  if (!msg) return null;
  return <p style={{ color: msg.ok ? '#16a34a' : '#dc2626', margin: '8px 0', fontSize: '14px' }}>{msg.text}</p>;
}

const FORMAT_LABELS = { pdf: 'PDF', xlsx: 'Excel', csv: 'CSV', html: 'HTML' };
const FORMAT_ICONS = { pdf: '📄', xlsx: '📊', csv: '📋', html: '🌐' };

// ============================================================
// Berichtstyp-Karte
// ============================================================
function ReportCard({ report, onGenerate }) {
  const [params, setParams] = useState({ von: '', bis: '', monat: '', jahr: '' });
  const [format, setFormat] = useState('pdf');
  const [loading, setLoading] = useState(false);

  const currentMonth = new Date().toISOString().slice(0, 7);
  const currentYear = new Date().getFullYear().toString();

  return (
    <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div>
        <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '4px' }}>{report.name}</div>
        <div style={{ fontSize: '13px', color: '#64748b' }}>{report.description}</div>
        {report.archive_count > 0 && (
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
            {report.archive_count} Bericht{report.archive_count !== 1 ? 'e' : ''} im Archiv
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {(report.formats || ['pdf']).map(f => (
          <button key={f} onClick={() => setFormat(f)}
            style={{ padding: '4px 10px', border: `1px solid ${format === f ? '#6366f1' : '#e2e8f0'}`, borderRadius: '6px', cursor: 'pointer', fontSize: '12px', background: format === f ? '#eff6ff' : '#f8fafc', color: format === f ? '#6366f1' : '#64748b', fontWeight: format === f ? '700' : '400' }}>
            {FORMAT_ICONS[f]} {FORMAT_LABELS[f] || f}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="month" style={inputStyle} value={params.monat} onChange={e => setParams(p => ({ ...p, monat: e.target.value, von: '', bis: '' }))} title="Monat" />
        <input type="number" style={{ ...inputStyle, width: '80px' }} value={params.jahr} onChange={e => setParams(p => ({ ...p, jahr: e.target.value, monat: '' }))} placeholder={currentYear} title="Jahr" min="2020" max="2030" />
        <span style={{ fontSize: '12px', color: '#94a3b8' }}>oder</span>
        <input type="date" style={inputStyle} value={params.von} onChange={e => setParams(p => ({ ...p, von: e.target.value, monat: '' }))} title="Von" />
        <input type="date" style={inputStyle} value={params.bis} onChange={e => setParams(p => ({ ...p, bis: e.target.value, monat: '' }))} title="Bis" />
      </div>

      <button
        disabled={loading}
        onClick={async () => {
          setLoading(true);
          await onGenerate(report.key, format, params);
          setLoading(false);
        }}
        style={{ ...btnStyle, alignSelf: 'flex-start' }}
      >
        {loading ? 'Erstelle…' : `${FORMAT_ICONS[format] || '📄'} ${FORMAT_LABELS[format] || format} erstellen`}
      </button>
    </div>
  );
}

// ============================================================
// Archiv-Tab
// ============================================================
function ArchivTab() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ format: '', typ: '' });
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit, offset, ...Object.fromEntries(Object.entries(filter).filter(([, v]) => v)) });
    api.get(`/reports/v2/archive?${params}`)
      .then(r => { setEntries(r.data.archive || []); setTotal(r.data.total || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [filter, offset]);

  useEffect(() => { load(); }, [load]);

  async function download(id) {
    window.open(`/api/reports/v2/archive/${id}/download`, '_blank');
  }

  async function deleteEntry(id) {
    if (!window.confirm('Archiveintrag löschen?')) return;
    try {
      await api.delete(`/reports/v2/archive/${id}`, { data: { bestaetigt: true } });
      load();
    } catch {}
  }

  if (loading) return <p>Lade Archiv…</p>;

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={{ ...inputStyle, width: 'auto' }} value={filter.format} onChange={e => { setFilter(f => ({ ...f, format: e.target.value })); setOffset(0); }}>
          <option value="">Alle Formate</option>
          {['pdf', 'xlsx', 'csv'].map(f => <option key={f} value={f}>{FORMAT_LABELS[f]}</option>)}
        </select>
        <select style={{ ...inputStyle, width: 'auto' }} value={filter.typ} onChange={e => { setFilter(f => ({ ...f, typ: e.target.value })); setOffset(0); }}>
          <option value="">Alle Typen</option>
          {['finanzuebersicht', 'monthly', 'yearly', 'transactions', 'categories', 'recipients', 'geldfresser', 'audit'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <button onClick={load} style={{ ...btnStyle, padding: '6px 12px' }}>Aktualisieren</button>
        <span style={{ fontSize: '13px', color: '#64748b' }}>{total} Einträge</span>
      </div>

      {entries.length === 0 ? (
        <p style={{ color: '#94a3b8', textAlign: 'center', padding: '40px' }}>Noch keine archivierten Berichte.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {entries.map(e => (
            <div key={e.id} style={{ ...cardStyle, padding: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '24px' }}>{FORMAT_ICONS[e.format] || '📄'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '600', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.titel}</div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  {e.format?.toUpperCase()} · {e.filesize ? `${Math.round(e.filesize / 1024)} KB` : '?'}
                  {e.zeitraum_von ? ` · ${e.zeitraum_von}` : ''}{e.zeitraum_bis ? `–${e.zeitraum_bis}` : ''}
                  {' · '}{e.created_at ? new Date(e.created_at).toLocaleString('de-DE') : '-'}
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'monospace' }}>{e.report_id}</div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => download(e.id)} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', background: '#f0f9ff', color: '#3b82f6' }}>Download</button>
                <button onClick={() => deleteEntry(e.id)} style={{ padding: '6px 10px', border: '1px solid #fee2e2', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', background: '#fff', color: '#dc2626' }}>Löschen</button>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
            <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - limit))} style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Zurück</button>
            <span style={{ fontSize: '13px', color: '#64748b' }}>{offset + 1}–{Math.min(offset + limit, total)} von {total}</span>
            <button disabled={offset + limit >= total} onClick={() => setOffset(o => o + limit)} style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Weiter</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Hauptkomponente
// ============================================================
export default function ReportCenterPage() {
  const [activeTab, setActiveTab] = useState('berichte');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get('/reports/v2/center/list')
      .then(r => setReports(r.data.reports || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function generate(reportType, format, params) {
    setMsg(null);
    try {
      const body = { report_type: reportType, format, archive: true, ...params };
      const resp = await api.post('/reports/v2/generate', body, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const cd = resp.headers['content-disposition'] || '';
      const fnMatch = cd.match(/filename="([^"]+)"/);
      a.href = url;
      a.download = fnMatch ? fnMatch[1] : `bericht_${reportType}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMsg({ ok: true, text: `${FORMAT_LABELS[format] || format}-Bericht erstellt und heruntergeladen.` });
    } catch (err) {
      let errText = 'Fehler beim Erstellen des Berichts';
      if (err.response?.data) {
        try {
          const text = await new Response(err.response.data).text();
          const parsed = JSON.parse(text);
          errText = parsed.error || errText;
        } catch {}
      }
      setMsg({ ok: false, text: errText });
    }
  }

  const tabs = [
    { key: 'berichte', label: 'Berichte erstellen' },
    { key: 'archiv', label: 'Archiv' },
  ];

  return (
    <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid #e2e8f0', paddingBottom: '0' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{ padding: '8px 20px', background: 'none', border: 'none', borderBottom: activeTab === t.key ? '2px solid #6366f1' : '2px solid transparent', marginBottom: '-2px', cursor: 'pointer', fontWeight: activeTab === t.key ? '700' : '400', color: activeTab === t.key ? '#6366f1' : '#64748b', fontSize: '14px' }}>
            {t.label}
          </button>
        ))}
      </div>

      <Msg msg={msg} />

      {activeTab === 'berichte' && (
        loading ? <p>Lade Berichtstypen…</p> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {reports.map(r => <ReportCard key={r.key} report={r} onGenerate={generate} />)}
          </div>
        )
      )}

      {activeTab === 'archiv' && <ArchivTab />}
    </div>
  );
}
